import React, { useEffect, useState, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
    StyledCard,
    StyledCardContent,
    Text,
    AvatarWithUploader,
    OutlinedButton,
    ContainedButton,
    HospitalInfoForm,
    Progress,
} from '../../index';

import { GET_HOSPITAL, UPDATE_HOSPITAL, DELETE_HOSPITAL_AVATAR } from '../../../services/provider';
import { deleteMedia, uploadMedia } from '../../../services/index';
import getFileFromUrl from '../../../utils/getFileFromUrl';
import { validEmail, validPhone } from '../../../utils/regex';
import { alertError, alertSuccess } from '../../../redux/stateActions';

const HospitalInfo = ({ canEdit }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [errors, setErrors] = useState([]);
    const [inputValue, setInputValue] = useState({
        avatar: '',
        name: '',
        hospitalID: '',
        email: '',
        phoneNumber: '',
        address: '',
    });
    const [parentReset, setParentReset] = useState(0);

    /*--------------- Hospital Detail ---------------*/
    const {
        data: getHospitalData,
        loading: getHospitalLoading,
        error: getHospitalError,
        refetch: getHospitalRefetch,
    } = useQuery(GET_HOSPITAL, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user),
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getHospitalError) {
            dispatch(alertError(`Cannot get Counseling Service: ${getHospitalError.message}`));
        }
        if (getHospitalData) {
            const { avatar, name, hospitalID, email, phoneNumber, address } =
                getHospitalData.hospital;
            setInputValue({
                avatar: avatar || '',
                name: name || '',
                hospitalID: hospitalID || '',
                email: email || '',
                phoneNumber: phoneNumber || '',
                address: address || '',
            });
        }
    }, [getHospitalError, getHospitalData]);

    const [
        updateHospital,
        { data: updateHospitalData, loading: updateHospitalLoading, error: updateHospitalError },
    ] = useMutation(UPDATE_HOSPITAL);

    useEffect(() => {
        if (updateHospitalError) {
            dispatch(
                alertError(`Cannot update Counseling Service: ${updateHospitalError.message}`)
            );
        }
        if (updateHospitalData) {
            dispatch(alertSuccess('Counseling Service Updated'));
            getHospitalRefetch();
        }
    }, [updateHospitalError, updateHospitalData]);

    const handleChange = (e) => {
        const newInputValue = { ...inputValue };
        const value = e.target.value;
        const name = e.target.name;
        newInputValue[name] = value;
        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const handleReset = () => {
        setInputValue(getHospitalData.hospital);
        setNewAvatar(null);
        setNewFileInfo(null);
        setParentReset(parentReset + 1);
    };

    const handleSave = () => {
        const newErrors = [];

        if (inputValue.name === '') {
            newErrors.push({
                property: 'name',
                message: 'Please provide a valid Counseling Service name.',
            });
        }

        if (!validEmail.test(inputValue.email)) {
            newErrors.push({
                property: 'email',
                message: 'Please provide a valid Email.',
            });
        }

        if (!validPhone.test(inputValue.phoneNumber)) {
            newErrors.push({
                property: 'phoneNumber',
                message: 'Please provide a valid Phone Number.',
            });
        }

        if (inputValue.address === '') {
            newErrors.push({
                property: 'address',
                message: 'Please provide a valid Address.',
            });
        }

        if (!_.isEmpty(newErrors)) return setErrors(newErrors);

        if (!_.isNull(newAvatar)) {
            uploadAvatar();
        }

        updateHospital({
            variables: {
                ...inputValue,
                name: inputValue.name.trim(),
                email: inputValue.email.trim(),
                address: inputValue.address.trim(),
                id: user.staff.hospital.id,
            },
        });
    };

    /*--------------- Update User Avatar ---------------*/
    const [newAvatar, setNewAvatar] = useState(null);
    const [newFileInfo, setNewFileInfo] = useState(null);

    const [
        deleteHospitalAvatar,
        { data: deleteHospitalAvatarData, error: deleteHospitalAvatarError },
    ] = useMutation(DELETE_HOSPITAL_AVATAR);

    useEffect(() => {
        if (deleteHospitalAvatarError) {
            dispatch(alertError(`Cannot delete user avatar: ${deleteHospitalAvatarError.message}`));
        }
    }, [deleteHospitalAvatarError, deleteHospitalAvatarData]);

    const uploadAvatar = async () => {
        const nameArr = newFileInfo.name.split('.');

        const myFile = await getFileFromUrl(
            newAvatar,
            `avatar_${getHospitalData.hospital.id}.${nameArr[nameArr.length - 1]}`,
            newFileInfo.type
        );

        const formData = new FormData();
        formData.append('files', myFile);
        formData.append('ref', 'Hospitals');
        formData.append('refId', getHospitalData.hospital.id);
        formData.append('field', 'avatar');

        if (!_.isNull(getHospitalData.hospital.avatar)) {
            try {
                deleteHospitalAvatar({
                    variables: {
                        id: getHospitalData.hospital.id,
                    },
                });
                await deleteMedia(getHospitalData.hospital.avatar.id);
            } catch (error) {
                console.log('deleteMedia error', error);
            }
        }

        try {
            const res = await uploadMedia(formData);

            if (!_.isEmpty(res)) {
                setNewAvatar(null);
                setNewFileInfo(null);
                dispatch(alertSuccess('Avatar Uploaded'));
                getHospitalRefetch();
            }
        } catch (error) {
            dispatch(
                alertError(`Cannot upload Avatar: ${JSON.stringify(error.response?.data?.message)}`)
            );
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            {(getHospitalLoading || updateHospitalLoading) && <Progress />}
            <StyledCard style={{ padding: '24px 30px' }}>
                <StyledCardContent
                    style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}
                >
                    <Text $fontSize="18px" $bold>
                        Counseling Service Details
                    </Text>
                    <div style={{ display: 'flex', columnGap: '35px' }}>
                        <div>
                            <AvatarWithUploader
                                alt={inputValue?.name}
                                src={inputValue.avatar ? inputValue.avatar.url : null}
                                $avatarSize="120px"
                                $borderRadius="50%"
                                setNewAvatar={setNewAvatar}
                                setNewFileInfo={setNewFileInfo}
                                parentReset={parentReset}
                                disabled={!canEdit}
                            />
                        </div>

                        <div style={{ flexGrow: 1 }}>
                            <HospitalInfoForm
                                data={inputValue}
                                onChange={handleChange}
                                errors={errors}
                                disabled={!canEdit}
                            />

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'flex-end',
                                    columnGap: '15px',
                                    flexGrow: 1,
                                    marginTop: '100px',
                                }}
                            >
                                {canEdit && (
                                    <Fragment>
                                        <OutlinedButton
                                            style={{ height: '32px', width: '93px' }}
                                            onClick={handleReset}
                                            $noShadow
                                        >
                                            Reset
                                        </OutlinedButton>
                                        <ContainedButton
                                            style={{ height: '32px', width: '93px' }}
                                            onClick={handleSave}
                                            $noShadow
                                        >
                                            Save
                                        </ContainedButton>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </StyledCardContent>
            </StyledCard>
        </div>
    );
};

HospitalInfo.propTypes = {
    canEdit: PropTypes.bool,
};

export default HospitalInfo;
